<template>
  <div ref="containerRef" class="chart-wrapper">
    <VChart class="chart" :option="chartOption" autoresize />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, defineComponent, computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'

use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default defineComponent({
  name: 'TimeGroupedBar',
  components: { VChart },
  props: {
    durations: {
      type: Object,
      required: true
    },
    timeSpan:{
      type: String,
      required: true
    },
    detectionZoneTypeName:{
      type: String,
      required: true
    },
  },
  setup(props) {
    const { t } = useI18n()
    const containerRef = ref(null)
    const chartWidth    = ref(0)
    
    const palette = computed(() => {
      return detection_zone_palettes[ props.detectionZoneTypeName ] ?? {
        segmentation: {
          walkBy:      '#B2C149',
          shortLinger: '#4F7EB3',
          clearLinger: '#425461'
        }
      }
    })

    function updateWidth() {
      if (containerRef.value)
        chartWidth.value = containerRef.value.clientWidth
    }

    onMounted(() => {
      updateWidth()
      window.addEventListener('resize', updateWidth)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateWidth)
    })

    const totalSeconds = computed(() =>
      (props.durations.walk_bys ?? 0) +
      (props.durations.short_lingerers ?? 0) +
      (props.durations.clear_lingerers ?? 0)
    )

    const timeUnit = computed(() => {
      if (totalSeconds.value >= 3600) return 'h'
      if (totalSeconds.value >= 300) return 'min'
      return 'sec'
    })

    const convert = (val) => {
      if (timeUnit.value === 'h') return +(val / 3600).toFixed(2)
      if (timeUnit.value === 'min') return +(val / 60).toFixed(1)
      return +val.toFixed(0)
    }

    const walk  = computed(() => convert(props.durations.walk_bys  ?? 0))
    const short = computed(() => convert(props.durations.short_lingerers ?? 0))
    const clear = computed(() => convert(props.durations.clear_lingerers ?? 0))

    // timeUnit stays 'h' | 'min' | 'sec' (it drives the conversion); only the shown
    // unit is translated ('h' → 'uur'). English output is unchanged.
    const unitLabel = () => t(`charts.units.${timeUnit.value}`)
    const nlNum = v => formatNumber(v, { decimals: Math.min(2, (String(v).split('.')[1] || '').length) })
    const valueText = v => currentLanguage() === 'en' ? v : nlNum(v)

    const chartOption = computed(() => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: params =>
          params.map(p => `${p.marker} ${p.seriesName}: ${valueText(p.value)} ${unitLabel()}`).join('<br>')
      },
      legend: {
        top: 0,
        textStyle: { color: '#425461' }
      },
      grid: {
        top: '17%',
        left: '5%',
        right: '0%',
        bottom: '0%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: [t('charts.totalDuration.category', { span: props.timeSpan })],
        axisLabel: { color: '#425461' },
        axisTick: { show: false },
        axisLine: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: currentLanguage() === 'en' ? `{value} ${timeUnit.value}` : v => `${nlNum(v)} ${unitLabel()}`,
          color: '#425461'
        },
        splitLine: { lineStyle: { color: '#e0e0e0' } },
        title: {
          text: t('charts.totalDuration.axisTitle', { unit: unitLabel() }),
          textStyle: { color: '#425461', fontWeight: 'bold' }
        }
      },
      series: [
        {
          name: t('charts.segments.walkBys'),
          type: 'bar',
          barGap: 0,
          barWidth: 60,
          data: [walk.value],
          itemStyle: { color: palette.value.segmentation.walkBy }
        },
        {
          name: t('charts.segments.shortLingerers'),
          type: 'bar',
          barWidth: 60,
          data: [short.value],
          itemStyle: { color: palette.value.segmentation.shortLinger }
        },
        {
          name: t('charts.segments.clearLingerers'),
          type: 'bar',
          barWidth: 60,
          data: [clear.value],
          itemStyle: { color: palette.value.segmentation.clearLinger }
        }
      ]
    }))

    return { chartOption }
  }
})
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  height: 460px; /* or whatever fixed height you want */
}
.chart {
  width: 100%;
  height: 100%;
}
</style>